import React from 'react'
import { node, object } from 'prop-types'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { useQuery } from '@apollo/client'
import LogInRedirect from '../pages/LogInRedirect'
import GET_USER from '../apollo/queries/GET_USER'
import { useIdleTimer } from 'react-idle-timer'
import Modal from '../components/common/Modal'
import TimeOutReminder from '../components/info/TimeOutReminder'
import { useEffect, useState, useContext, createContext } from 'react'
import GetRefreshToken from '../components/info/GetRefreshToken'
import useInterval from './useInterval'
import NotFoundPage from '../pages/404'
import { CALENDAR_ARTICLE_LOCAL_STORAGE_KEY } from '../context/CalendarArticleContext'

// check if we are in a browser for ssr
const isWindowClient = typeof window !== 'undefined'

const getJwtToken = () => {
  if (!isWindowClient) {
    return
  }
  //try to get token from query string
  let query = new URLSearchParams(location.search)
  let token = query.get('token')

  if (token) {
    //get valid token from query string and store it
    localStorage.setItem('token', token)
    return
  }
}

const getExpireDurationInMilliseconds = expireTime => {
  return new Date(expireTime).getTime() - new Date().getTime()
}

const AuthContext = createContext()

const AuthProvider = ({ children, defaultAuthState }) => {
  //TODO: Make default timeout to be a configurable variable, currently default is 15 mins
  const defaultTimeoutOut = 15 * 60
  const [authState, setAuthState] = useState(defaultAuthState)
  const [showTimeoutModel, setShowTimeoutModel] = useState(false)
  const [showGetNewToken, setShowGetNewToken] = useState(false)

  getJwtToken()

  //function is being called when user is active, for details on what defined as 'active', refer: https://www.npmjs.com/package/react-idle-timer
  const handleOnAction = () => {
    if (showGetNewToken) {
      setShowGetNewToken(!showGetNewToken)
    }
  }

  const handleOnIdle = () => {
    //no need to get a new token every 15 mins, when user is inactive
    if (showGetNewToken) {
      setShowGetNewToken(!showGetNewToken)
    }
    //user is has been inactive for 15 mins, show timeoutModel
    if (!showTimeoutModel) {
      setShowTimeoutModel(!showTimeoutModel)
    }
  }

  useInterval(() => {
    //get new token every 15 mintues as long as model is not open
    if (!showTimeoutModel) {
      setShowGetNewToken(!showGetNewToken)
    }
  }, defaultTimeoutOut * 1000)

  useIdleTimer({
    //timeout unit is in milliseconds
    timeout: defaultTimeoutOut * 1000,
    onIdle: handleOnIdle,
    onAction: handleOnAction,
    debounce: 500,
  })

  const { data, loading, error } = useQuery(GET_USER())
  useEffect(() => {
    if (data?.user && data.user.expiration) {
      const expirationTimeLeft = getExpireDurationInMilliseconds(
        data.user.expiration
      )
      //get a new token if token will expire within 15 mins
      if (expirationTimeLeft / (1000 * 60) < defaultTimeoutOut / 60) {
        setShowGetNewToken(true)
      }
    }

    if (data?.user)
      setAuthState({
        status: 'success',
        user: data?.user,
        error: null,
      })
  }, [data?.user])

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    if (/unauthenticated/i.test(error?.message)) {
      return <LogInRedirect />
    }
    return <NotFoundPage />
  }

  return (
    <AuthContext.Provider value={authState}>
      <div>
        <div>{children}</div>
        <Modal
          label="session timeout dialog"
          handleDismiss={() => setShowTimeoutModel(false)}
          isOpen={showTimeoutModel}
        >
          <TimeOutReminder handleClose={() => setShowTimeoutModel(false)} />
        </Modal>
        {showGetNewToken && <GetRefreshToken />}
      </div>
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: node.isRequired,
  defaultAuthState: object.isRequired,
}

AuthProvider.defaultProps = {
  children: node.isRequired,
  defaultAuthState: {
    status: 'pending',
    error: null,
    user: null,
  },
}

const useAuth = () => {
  const state = useContext(AuthContext)
  const isPending = state?.status === 'pending'
  const isError = state?.status === 'error'
  const isSuccess = state?.status === 'success'
  const isAuthenticated = state?.user && isSuccess
  const user = state?.user
  const isAdmin = state?.user?.role?.name === 'Admin'

  useEffect(() => {
    if (!isAuthenticated)
      localStorage.removeItem(CALENDAR_ARTICLE_LOCAL_STORAGE_KEY)
  }, [isAuthenticated])

  return {
    ...state,
    isPending,
    isError,
    isSuccess,
    isAuthenticated,
    user,
    isAdmin,
  }
}

export { AuthProvider, useAuth }
